import { useEffect, useMemo, useReducer, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import biApi from "../../../../../../../../../api/biApi";
import type { NumberFormatterOptions } from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  ChartOfAccounts,
  ConditionField,
  MeasureDataset,
  MeasureDescriptor,
} from "../../../../../../../../../api/types/svTypes.ts";
import { selectAccountBookSettings } from "../../../../../store/selectors/clientSettingsSelectors";
import { selectMeasureDatasets } from "../../../../../store/selectors/measureDataSetsSelectors";
import {
  selectDimensions,
  selectFunctions,
  selectPostingChartOfAccounts,
} from "../../../../../store/selectors/metaDataSelectors";
import { createMeasureDatasetsAction, createMetaDataAction } from "../../../../../store/utils/storeUtils";
import { toAmountType, toCalculateBy } from "../../../../../utilities/measureConverter.ts";
import type { CustomMeasureState } from "../types/customMeasure.types";
import { initialState } from "../types/customMeasure.types";
import { CustomMeasureActionType } from "../types/customMeasureStateActions";
import type { FormulaNode } from "../utilities/fillNodes";
import type { INode } from "../utilities/formulaParser";
import { isFormulaValid } from "../utilities/formulaValid";
import { toFormulaNodes } from "../utilities/measureConverter";
import type { Token } from "../utilities/tokenizer";
import { getAvailableDimensions } from "../utilities/variables.utils";
import { CustomMeasureReducer } from "./CustomMeasureReducer";

export type CustomMeasureReturnType = CustomMeasureState & {
  formulaValid: boolean;
  chartOfAccounts?: ChartOfAccounts;
  actions: {
    updateCaption: (caption: string) => void;
    update: (changes: Partial<CustomMeasureState>) => void;
    setDataSets: (dataSets: MeasureDataset[]) => void;
    setRootNode: (rootNode?: INode) => void;
    updateFormulaNode: (node: FormulaNode, changes: Partial<FormulaNode>) => void;
    addConditions: (node: FormulaNode, newConditions: ConditionField[]) => void;
    updateCondition: (node: FormulaNode, condition: ConditionField, changes: Partial<ConditionField>) => void;
    removeCondition: (node: FormulaNode, condition: ConditionField) => void;
    highlightNode: (token: Token | undefined) => void;
    functionHovered: (token: Token | undefined) => void;
    setSelectedFormulaParameter: (token: Token | undefined) => void;
  };
  dataLoadingError: string | undefined;
  loading: boolean;
  datasetError: string | undefined;
};
interface Props {
  measure?: MeasureDescriptor;
  defaultGroup?: string;
  measureFormat?: NumberFormatterOptions | undefined;
}
export const useCustomMeasure = ({ measure, defaultGroup, measureFormat }: Props): CustomMeasureReturnType => {
  const dispatch = useDispatch();
  const [state, dispatchCustom] = useReducer(CustomMeasureReducer, initialState);
  const chartOfAccounts = useSelector(selectPostingChartOfAccounts);
  const accountingBookSettings = useSelector(selectAccountBookSettings);

  const formulaValid = useMemo(() => isFormulaValid(state), [state]);

  const datasetsState = useSelector(selectMeasureDatasets);
  const dimensions = useSelector(selectDimensions);
  const functions = useSelector(selectFunctions);

  const [dataLoadingError, setDataLoadingError] = useState<string>();
  const [datasetError, setDatasetError] = useState<string>();

  const actions = useMemo(() => {
    return {
      updateCaption: (caption: string) =>
        dispatchCustom({ type: CustomMeasureActionType.UPDATE_CAPTION, payload: caption }),
      update: (changes: Partial<CustomMeasureState>) =>
        dispatchCustom({ type: CustomMeasureActionType.UPDATE, payload: changes }),
      setDataSets: (dataSets: MeasureDataset[]) =>
        dispatchCustom({ type: CustomMeasureActionType.SET_DATASETS, payload: dataSets }),
      setRootNode: (rootNode?: INode) =>
        dispatchCustom({ type: CustomMeasureActionType.SET_ROOTNODE, payload: rootNode }),
      updateFormulaNode: (node: FormulaNode, changes: Partial<FormulaNode>) =>
        dispatchCustom({ type: CustomMeasureActionType.UPDATE_FORMULA_NODE, payload: { node, changes } }),
      addConditions: (node: FormulaNode, newConditions: ConditionField[]) =>
        dispatchCustom({ type: CustomMeasureActionType.ADD_CONDITIONS, payload: { node, newConditions } }),
      updateCondition: (node: FormulaNode, condition: ConditionField, changes: Partial<ConditionField>) =>
        dispatchCustom({
          type: CustomMeasureActionType.UPDATE_CONDITION,
          payload: { node, condition, changes },
        }),
      removeCondition: (node: FormulaNode, condition: ConditionField) =>
        dispatchCustom({ type: CustomMeasureActionType.REMOVE_CONDITION, payload: { node, condition } }),
      highlightNode: (token: Token | undefined) =>
        dispatchCustom({ type: CustomMeasureActionType.HIGHLIGHT_NODE, payload: token }),
      functionHovered: (token: Token | undefined) =>
        dispatchCustom({ type: CustomMeasureActionType.FUNCTION_HOVERED, payload: token }),
      setSelectedFormulaParameter: (token: Token | undefined) =>
        dispatchCustom({ type: CustomMeasureActionType.FORMULA_PARAMETER_CLICK, payload: token }),
    };
  }, []);

  useEffect(() => {
    if (datasetsState.loaded) {
      actions.setDataSets(datasetsState.datasets);
      return;
    }
    dispatch(createMeasureDatasetsAction("update", { loading: true }));
    biApi
      .getMeasureDataSets()
      .then((result) => {
        if (result.success) {
          dispatch(createMeasureDatasetsAction("update", { datasets: result.data, loaded: true }));
          actions.setDataSets(result.data);
        } else {
          setDatasetError("Measure DataSets loading failed...");
        }
      })
      .catch(() => setDatasetError("Measure DataSets loading failed..."))
      .finally(() => dispatch(createMeasureDatasetsAction("update", { loading: false })));
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  useEffect(() => {
    if (chartOfAccounts === undefined) {
      dispatch(createMeasureDatasetsAction("update", { loading: true }));
      biApi
        .getChartOfAccounts()
        .then((result) => {
          if (result.success) {
            dispatch(createMetaDataAction("setChartOfAccounts", result.data));
          } else {
            setDataLoadingError("Encountering problems while loading the data");
          }
        })
        .catch(() => setDataLoadingError("Encountering problems while loading the data"))
        .finally(() => dispatch(createMeasureDatasetsAction("update", { loading: false })));
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [chartOfAccounts]);

  useEffect(() => {
    const filteredDictionaries = getAvailableDimensions(dimensions, accountingBookSettings?.multiBook === true);

    dispatchCustom({
      type: CustomMeasureActionType.UPDATE_DIMENSIONS,
      payload: { dimensions: filteredDictionaries },
    });
  }, [accountingBookSettings?.multiBook, dimensions]);

  useEffect(() => {
    dispatchCustom({ type: CustomMeasureActionType.UPDATE, payload: { functions } });
  }, [functions]);

  useEffect(() => {
    let measureData: Partial<CustomMeasureState> = {};
    if (measureFormat) {
      measureData.format = measureFormat;
    }

    if (measure !== undefined) {
      measureData = {
        ...measureData,
        formula: measure.formula,
        caption: measure.caption,
        group: measure.group || defaultGroup,
        subgroupId: measure.subgroupId,
        calculateBy: toCalculateBy(measure.units),
        formulaNodes: toFormulaNodes(measure.units, dimensions),
        amountType: toAmountType(measure.units),
        isConfigurable: measure.capabilities.isConfigurable,
      };
    } else {
      measureData = { ...measureData, group: defaultGroup };
    }

    dispatchCustom({ type: CustomMeasureActionType.UPDATE, payload: measureData });
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [measure, defaultGroup, measureFormat]);

  return {
    ...state,
    formulaValid,
    chartOfAccounts,
    actions,
    dataLoadingError,
    loading: datasetsState.loading,
    datasetError,
  };
};
